import React, { useEffect, useState } from "react";
import { IntegrationsService } from "../../services/IntegrationsService";
import { usePermissions } from "../../PermissionsProvider";
import SlackIntegration from "../../models/SlackIntegration";
import { RotateCcwIcon, TrashIcon, LinkIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Skeleton } from "../ui/skeleton";

const DevelopersIntegrations: React.FC = () => {
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [slackIntegration, setSlackIntegration] = useState<SlackIntegration | null>(null);

  const slackInstallUrl =
    "https://slack.com/oauth/v2/authorize?client_id=7076808695920.7108175715302&scope=app_mentions:read,channels:join,channels:manage,channels:read,chat:write,commands,groups:read,groups:write,im:write,mpim:write,team:read,channels:history,groups:history,mpim:history,im:history&user_scope=";

  useEffect(() => {
    IntegrationsService.getSlackIntegration(setLoading, apiKey).subscribe(
      (slackIntegration: SlackIntegration) => {
        setSlackIntegration(slackIntegration);
      },
      (error: any) => {
        console.log(error);
        setLoading(false);
      }
    );
  }, []);

  const handleSlackConnect = () => {
    window.open(slackInstallUrl, "_blank");
  };

  const handleSlackDisconnect = () => {
    // Placeholder for disconnect functionality
    console.log("Disconnect functionality not yet implemented");
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-gc-med text-foreground">Integrations</h3>
        <p className="text-sm text-muted-foreground">Connect external services to enhance your workflow</p>
      </div>

      {loading ? (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-6 rounded" />
              <Skeleton className="h-6 w-20" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-32" />
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <RotateCcwIcon className="w-5 h-5 text-maitai-green" />
              Slack Integration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {slackIntegration ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                    <LinkIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-gc-med text-foreground">Connected to Slack</p>
                    <p className="text-xs text-muted-foreground">
                      Team ID: {slackIntegration.team_id || "Unknown"}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Your Slack workspace is connected and ready to receive notifications.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleSlackDisconnect}
                    disabled={loading}
                    className="gap-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Disconnect Slack
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                    <RotateCcwIcon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-gc-med text-foreground">Slack Integration</p>
                    <p className="text-xs text-muted-foreground">Not connected</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Connect your Slack workspace to receive real-time notifications and updates.
                  </p>
                  <Button
                    onClick={handleSlackConnect}
                    disabled={loading}
                    className="gap-2"
                  >
                    <LinkIcon className="w-4 h-4" />
                    Connect to Slack
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DevelopersIntegrations;
